import { type FC, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import {
  Button,
  classNames,
  Icon,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import { queryKeys } from "~/v2/api/queryKeys";
import {
  type DocumentRequestResponse,
  fetchAccountAgreement,
  uploadAccountDocument,
} from "~/v2/api/rest/account";
import { acceptW8 } from "~/v2/api/rest/onboarding";

interface W8BenFormProps {
  accountId: string;
  documentRequest?: DocumentRequestResponse;
}

export const W8BenForm: FC<W8BenFormProps> = ({
  accountId,
  documentRequest,
}) => {
  const queryClient = useQueryClient();
  const [viewedW8, setViewedW8] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  const handleViewW8Ben = async () => {
    try {
      const resp = await fetchAccountAgreement(accountId, "w_8ben");
      const binaryData = [];
      binaryData.push(resp);
      const fileURL = window.URL.createObjectURL(
        new Blob(binaryData, { type: "application/pdf" }),
      );
      window.open(fileURL);
      setViewedW8(true);
    } catch (err) {
      toastError(
        `Error fetching W-8BEN form: ${(err as Error)?.message || "Unknown error"}`,
        {},
      );
    }
  };

  const handleAcceptW8Ben = async () => {
    setIsAccepting(true);
    try {
      if (documentRequest) {
        const formBlob = await fetchAccountAgreement(accountId, "w_8ben");
        await uploadAccountDocument(documentRequest.id, {
          doc_type: "w_8ben",
          doc_sub_type: "Form W-8BEN",
          content: formBlob,
        });

        toastSuccess("Form W-8BEN verification completed successfully.");
      } else {
        // Without a document request, accepted without uploading a form
        await acceptW8(accountId);
        toastSuccess("Form W-8BEN form has been accepted.");
      }

      queryClient.invalidateQueries({
        queryKey: queryKeys.documents.requests(accountId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.documents.uploads(accountId),
      });
    } catch (err) {
      toastError(
        `Error accepting W-8BEN: ${(err as Error)?.message || "Unknown error"}`,
        {},
      );
    } finally {
      setIsAccepting(false);
    }
  };

  return (
    <div className="w-full max-w-xl rounded-lg border border-gray-200 bg-white p-6">
      <div>
        <div className="mb-1 font-medium text-gray-900">Form W-8BEN</div>
        <div className="mb-4 text-gray-600 text-sm">
          I confirm and certify that the information provided in the W-8BEN form
          is true, accurate, and complete. By clicking "I Agree" below, I
          acknowledge that I am electronically signing the W-8BEN form.
        </div>
        <Button
          type="button"
          variant="alternate"
          onClick={handleViewW8Ben}
          className="mb-4"
        >
          View Form W-8BEN
          <Icon name="ArrowTopRightOnSquare" className="ml-2" />
        </Button>
        <div className="flex items-center">
          <input
            id="w8ben-agree"
            type="checkbox"
            className="mr-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!viewedW8 || isAccepting}
            onChange={(e) => {
              if (e.target.checked) {
                handleAcceptW8Ben();
              }
            }}
          />
          <label
            htmlFor="w8ben-agree"
            className={classNames(
              `select-none text-sm ${
                !viewedW8 || isAccepting
                  ? "cursor-not-allowed text-gray-400"
                  : "cursor-pointer text-gray-900"
              }`,
            )}
          >
            I agree to electronically signing the W8-BEN form.
          </label>
        </div>
      </div>
    </div>
  );
};
